import { THEMATIC_ATTRIBUTES } from "../model/inp/thematicAttributes.ts";
import { formatUnit } from "../model/inp/properties.ts";
import { MAP_QUERY_COMPARATORS, type MapQueryComparator } from "./mapQuery.ts";
import { useT } from "../i18n/hooks.ts";

export function MapQueryBar({
  attributeKey,
  onAttributeChange,
  comparator,
  onComparatorChange,
  thresholdInput,
  onThresholdChange,
  matchCount,
  totalCount,
  onSelectMatches,
  onClose,
  flowUnits,
}: {
  attributeKey: string | null;
  onAttributeChange: (key: string | null) => void;
  comparator: MapQueryComparator;
  onComparatorChange: (comparator: MapQueryComparator) => void;
  thresholdInput: string;
  onThresholdChange: (value: string) => void;
  matchCount: number;
  totalCount: number;
  onSelectMatches: () => void;
  onClose: () => void;
  /** Shown as a unit suffix next to the threshold input — without it, a query like "Diámetro > 10" gives no way to tell what unit 10 is in. */
  flowUnits: string;
}) {
  const t = useT();
  const queryable = THEMATIC_ATTRIBUTES.filter((a) => !a.requiresResults);
  const optionLabel = (a: (typeof THEMATIC_ATTRIBUTES)[number]) => {
    const unit = formatUnit(a.unitKind, flowUnits);
    return unit ? `${t(a.labelKey)} (${unit})` : t(a.labelKey);
  };
  const byCategory = {
    node: queryable.filter((a) => a.category === "node"),
    link: queryable.filter((a) => a.category === "link"),
    subcatchment: queryable.filter((a) => a.category === "subcatchment"),
  };
  const hasSelection = attributeKey !== null;

  return (
    <div className="results-animation-bar map-query-bar">
      <select
        value={attributeKey ?? ""}
        onChange={(e) => onAttributeChange(e.target.value || null)}
        aria-label={t("mapQuery.attributePicker")}
      >
        <option value="">{t("thematicMap.pickAttribute")}</option>
        <optgroup label={t("thematicMap.categoryNode")}>
          {byCategory.node.map((a) => (
            <option key={a.key} value={a.key}>
              {optionLabel(a)}
            </option>
          ))}
        </optgroup>
        <optgroup label={t("thematicMap.categoryLink")}>
          {byCategory.link.map((a) => (
            <option key={a.key} value={a.key}>
              {optionLabel(a)}
            </option>
          ))}
        </optgroup>
        <optgroup label={t("thematicMap.categorySubcatchment")}>
          {byCategory.subcatchment.map((a) => (
            <option key={a.key} value={a.key}>
              {optionLabel(a)}
            </option>
          ))}
        </optgroup>
      </select>
      {hasSelection &&
        (() => {
          const selectedAttribute = queryable.find((a) => a.key === attributeKey);
          const selectedUnit = selectedAttribute
            ? formatUnit(selectedAttribute.unitKind, flowUnits)
            : "";
          return (
            <>
              <select
                value={comparator}
                onChange={(e) => onComparatorChange(e.target.value as MapQueryComparator)}
                className="map-query-comparator"
                aria-label={t("mapQuery.comparator")}
              >
                {MAP_QUERY_COMPARATORS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={thresholdInput}
                onChange={(e) => onThresholdChange(e.target.value)}
                className="map-query-value"
                aria-label={t("mapQuery.threshold")}
              />
              {selectedUnit && <span>{selectedUnit}</span>}
              <span className="results-animation-label">
                {t("mapQuery.matchCount", { count: matchCount, total: totalCount })}
              </span>
              <button className="button" onClick={onSelectMatches} disabled={matchCount === 0}>
                {t("mapQuery.selectMatches")}
              </button>
            </>
          );
        })()}
      <button className="button" onClick={onClose}>
        {t("common.close")}
      </button>
    </div>
  );
}
